import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { useSelector } from 'react-redux';
import { Swords } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Pagination from '@/components/Pagination';
import { PageLayout } from '../../../components/ui/PageLayout';
import { LoadingState } from '../../../components/AsyncStates';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import TournamentMatchTable from './components/TournamentMatchTable';
import MatchDetailModal from './components/MatchDetailModal';
import MatchFormModal from './components/MatchFormModal';
import DeleteMatchModal from './components/DeleteMatchModal';
import CompleteMatchModal from './components/CompleteMatchModal';
import type { CompleteMatchOutcome } from './components/CompleteMatchModal';
import ChangeStatusModal from './components/ChangeStatusModal';
import RescheduleMatchModal from './components/RescheduleMatchModal';
import BulkImportModal from './components/BulkImportModal';
import ClawbackConsentDialog from '../shared/ClawbackConsentDialog';
import { useClawbackConsent } from '../shared/useClawbackConsent';
import BracketTab from './components/BracketTab';
import StandingsTab from './components/StandingsTab';
import { useMatches } from '@/hooks/useMatches';
import { useTournament } from '@/hooks/useTournament';
import { useAllTournaments } from '@/hooks/useTournaments';
import { useAllReferees } from '@/hooks/useReferees';
import { completeMatch, updateMatchStatus, archiveMatch, getMatch, updateMatchAttachment } from '@/api/TournamentManagement/matchApi';
import { MatchStatus, TournamentFormat, AgeCategory, resolveEnum } from '@/models/tournament';
import type { MatchView, MatchListQuery } from '@/models/tournament';
import { Roles } from '@/models/general';
import { appDate } from '@/utils/appDateFormat';

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

const TournamentMatches: React.FC = () => {
  const { t } = useTranslation('tournament');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tournamentIdParam = searchParams.get('tournamentId') ? parseInt(searchParams.get('tournamentId')!) : undefined;
  const editMatchIdParam = searchParams.get('editMatchId') ? parseInt(searchParams.get('editMatchId')!) : undefined;
  const userRole = useSelector((s: any) => s.user.role) as string | undefined;
  const currentUserId = useSelector((s: any) => s.user.userId) as string | undefined;
  const isHrOrAdmin = userRole === Roles.Hr || userRole === Roles.Admin;
  const isReferee = !isHrOrAdmin;
  // CO-921: on a played match, officials, overrides, pricing inputs and delete are HR only.
  const isHr = userRole === Roles.Hr;

  // When the route carries a tournamentId the page is scoped to that tournament;
  // otherwise HR can pick via the in-page filter.
  const [tournamentFilter, setTournamentFilter] = useState<string>('');
  const tournamentId = tournamentIdParam ?? (tournamentFilter ? parseInt(tournamentFilter) : undefined);
  const { data: tournament } = useTournament(tournamentId ?? 0);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [pitchFilter, setPitchFilter] = useState<string>('');
  const [ageCategoryFilter, setAgeCategoryFilter] = useState<string>('');
  const [divisionFilter, setDivisionFilter] = useState<string>('');
  const [refereeFilter, setRefereeFilter] = useState<string>('');

  // Option sources for the new filters
  const { data: tournamentsList } = useAllTournaments();
  const { data: refereesList } = useAllReferees();
  const tournamentOptions = useMemo(() => [
    { value: '', label: t('All Tournaments') },
    ...((tournamentsList?.items ?? []).map((tour: any) => ({ value: String(tour.id), label: tour.name }))),
  ], [tournamentsList, t]);
  const refereeOptions = useMemo(() => [
    { value: '', label: t('All Officials') },
    ...((refereesList?.items ?? []).map((r: any) => ({ value: r.userId, label: r.name }))),
  ], [refereesList, t]);

  const [activeTab, setActiveTab] = useState<'matches' | 'bracket' | 'standings'>('matches');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'scheduledDateTime', direction: 'asc' });
  const [selectedMatch, setSelectedMatch] = useState<MatchView | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<MatchStatus | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Toast for user feedback on inline actions
  const { toast, showToast, dismissToast } = useToast();
  // CO-921/CO-922 Round 2: delete, update results and status changes can take back pay already given;
  // the server refuses until HR approves the exact amounts in this dialog.
  const clawback = useClawbackConsent();

  // Build query for API
  const query: MatchListQuery = useMemo(() => ({
    tournamentId,
    search: searchTerm || undefined,
    status: statusFilter !== '' ? (parseInt(statusFilter) as MatchStatus) : undefined,
    location: locationFilter || undefined,
    pitch: pitchFilter || undefined,
    ageCategory: ageCategoryFilter !== '' ? (parseInt(ageCategoryFilter) as AgeCategory) : undefined,
    division: divisionFilter || undefined,
    refereeUserId: isHrOrAdmin && refereeFilter ? refereeFilter : undefined,

    sortBy: sortConfig.key || undefined,
    sortDescending: sortConfig.direction === 'desc' || undefined,
    page: currentPage,
    pageSize: itemsPerPage,
  }), [tournamentId, searchTerm, statusFilter, locationFilter, pitchFilter, ageCategoryFilter, divisionFilter, refereeFilter, isHrOrAdmin, sortConfig.key, sortConfig.direction, currentPage, itemsPerPage]);

  const { data, loading, error, refetch } = useMatches(query);

  const matches = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const availableLocations = data?.availableLocations ?? [];
  const availablePitches = data?.availablePitches ?? [];
  const availableDivisions = data?.availableDivisions ?? [];

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleViewDetails = (match: MatchView) => {
    setSelectedMatch(match);
    setShowDetailModal(true);
  };

  const handleEditMatch = (match: MatchView) => {
    setSelectedMatch(match);
    setShowEditModal(true);
  };

  const handleDeleteMatch = async (matchId: number) => {
    try {
      const outcome = await clawback.run((consent) => archiveMatch(matchId, { clawbackConsent: consent }));
      if (outcome.status !== 'done') {
        // Declined: the match stays and both dialogs close. Not HR: say why; nothing changed.
        if (outcome.status === 'requiresHr') showToast(outcome.message, 'error');
        setShowDeleteModal(false);
        setSelectedMatch(null);
        return;
      }
      const result = outcome.result;
      if (result.errors.length > 0) {
        showToast(result.errors[0]?.description || t('Failed to delete match'), 'error');
        return;
      }
      setShowDeleteModal(false);
      setSelectedMatch(null);
      refetch();
    } catch (error: unknown) {
      showToast(getApiErrorMessage(error, t, t('Failed to delete match. Please try again.'), ' '), 'error');
    }
  };

  const handleCompleteMatch = async (completionData: {
    matchId: number;
    team1Score: number;
    team2Score: number;
    extraTimeTeam1Score?: number;
    extraTimeTeam2Score?: number;
    penaltyTeam1Score?: number;
    penaltyTeam2Score?: number;
    comments?: string;
    refereeRatings?: { refereeTeamMemberId: number; rating: number }[];
    attachment?: { file: File | null; remove: boolean; dirty: boolean };
  }): Promise<CompleteMatchOutcome> => {
    try {
      const outcome = await clawback.run((consent) => completeMatch({
        matchId: completionData.matchId,
        team1Score: completionData.team1Score,
        team2Score: completionData.team2Score,
        extraTimeTeam1Score: completionData.extraTimeTeam1Score,
        extraTimeTeam2Score: completionData.extraTimeTeam2Score,
        penaltyTeam1Score: completionData.penaltyTeam1Score,
        penaltyTeam2Score: completionData.penaltyTeam2Score,
        comments: completionData.comments,
        refereeRatings: completionData.refereeRatings,
      }, { clawbackConsent: consent }));
      // Declined or not HR: the results dialog stays open with what was typed; nothing was saved.
      if (outcome.status === 'declined') return { ok: false };
      if (outcome.status === 'requiresHr') return { ok: false, error: outcome.message };
      const result = outcome.result;
      if (result.errors.length > 0) {
        return { ok: false, error: result.errors[0]?.description || t('Failed to complete match') };
      }

      // Two-step save: metadata done; apply attachment changes if any.
      if (completionData.attachment?.dirty) {
        try {
          const attachResult = await updateMatchAttachment({
            matchId: completionData.matchId,
            file: completionData.attachment.file,
            remove: completionData.attachment.remove,
          });
          if (attachResult.errors.length > 0) {
            showToast(attachResult.errors[0]?.description || t('Match saved. Image upload failed — please retry.'), 'error');
          }
        } catch {
          showToast(t('Match saved. Image upload failed — please retry.'), 'error');
        }
      }

      setShowCompleteModal(false);
      setSelectedMatch(null);
      refetch();
      return { ok: true };
    } catch (error: unknown) {
      return { ok: false, error: getApiErrorMessage(error, t, t('Failed to complete match. Please try again.'), ' ') };
    }
  };

  /**
   * CO-809 — owner decision 2026-09-22, verbatim: "all match officials should be able to rate each
   * other." SUPERSEDES the 2026-09-08 rule that seated only HR/Admin and the match's own
   * Commissioner. Any official assigned to this match may now rate the others, and nobody rates
   * themselves (handled inside the dialog, which drops the caller's own row, and enforced by the
   * server on `RefereeRatings` regardless of what the client sends).
   *
   * Mirrors `MatchService.CompleteMatch` (T-05): `isHrOrAdmin || assigned && !archived`. The
   * server never returns archived members on the roster, so membership here IS non-archived
   * membership.
   */
  const canRateReferees = isHrOrAdmin || (selectedMatch?.refereeTeamMembers ?? []).some(
    (m) => m.userId === currentUserId,
  );

  /** Loads the freshest copy of a match into the Complete Match dialog (scores + team). */
  const openCompleteModal = async (match: MatchView) => {
    try {
      const result = await getMatch(match.id);
      setSelectedMatch(result.errors.length === 0 && result.payload ? result.payload : match);
    } catch {
      setSelectedMatch(match);
    }
    setShowCompleteModal(true);
  };

  /**
   * Status dropdown: the table only offers the server's allowed targets. A transition INTO
   * Completed carries the point calculation, so it goes through the Complete Match dialog (scores);
   * every other target is confirmed in ChangeStatusModal, which collects the reason (mandatory for
   * a cancellation — points and any generated payroll are voided).
   */
  const handleChangeStatus = (match: MatchView, newStatus: MatchStatus) => {
    if (newStatus === MatchStatus.Completed) {
      void openCompleteModal(match);
      return;
    }
    setSelectedMatch(match);
    setPendingStatus(newStatus);
  };

  const submitStatusChange = async (matchId: number, newStatus: MatchStatus, reason: string | undefined) => {
    try {
      const outcome = await clawback.run((consent) => updateMatchStatus({ matchId, newStatus, reason }, { clawbackConsent: consent }));
      // Declined: the status stays and the status dialog stays open. Not HR: say why.
      if (outcome.status === 'declined') return;
      if (outcome.status === 'requiresHr') {
        showToast(outcome.message, 'error');
        return;
      }
      const result = outcome.result;
      if (result.errors.length > 0) {
        showToast(result.errors[0]?.description || t('Failed to update match status'), 'error');
        return;
      }
      setPendingStatus(null);
      setSelectedMatch(null);
      refetch();
    } catch (error: unknown) {
      // Restoring a cancelled match can be refused (e.g. an overlapping referee booking) with a 400.
      showToast(getApiErrorMessage(error, t, t('Failed to update match status. Please try again.'), ' '), 'error');
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setLocationFilter('');
    setPitchFilter('');
    setAgeCategoryFilter('');
    setDivisionFilter('');
    setRefereeFilter('');
    if (!tournamentIdParam) setTournamentFilter('');
    setCurrentPage(1);
  };

  // Deep-link: open the edit-match modal when `editMatchId` query param is provided
  // (e.g. navigating from a referee assignment accepted/declined notification).
  useEffect(() => {
    if (!editMatchIdParam || isReferee) return;
    let cancelled = false;
    getMatch(editMatchIdParam)
      .then(result => {
        if (cancelled) return;
        if (result.errors.length === 0 && result.payload) {
          setSelectedMatch(result.payload);
          setShowEditModal(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [editMatchIdParam, isReferee]);

  return (
    <PageLayout
      title={tournament ? tournament.name : t('Matches')}
      description={tournament
        ? `${appDate(tournament.startDate)} — ${appDate(tournament.endDate)} · ${t(
            resolveEnum(TournamentFormat, tournament.format) === TournamentFormat.Knockout ? 'Knockout'
            : resolveEnum(TournamentFormat, tournament.format) === TournamentFormat.GroupStage ? 'Group Stage'
            : 'League'
          )}`
        : t('Manage tournament matches and team member assignments')
      }
      icon={Swords}
      accentColor="amber"
      maxWidth="1600px"
      headerActions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/tournament-management/tournaments')}
            data-testid="matches-back-btn"
          >
            <Icon name="ArrowLeft" size={16} className="me-2 rtl:rotate-180" />
            {t('Back')}
          </Button>
          {isHrOrAdmin && tournamentId && (
            <Button
              variant="outline"
              onClick={() => setShowImportModal(true)}
              data-testid="match-import-btn"
            >
              <Icon name="Upload" size={16} className="me-2" />
              {t('Import Excel')}
            </Button>
          )}
          {isHrOrAdmin && (
            <Button
              variant="outline"
              onClick={() => setShowAddModal(true)}
              data-testid="match-add-btn"
            >
              <Icon name="Plus" size={16} className="me-2" />
              {t('Add Match')}
            </Button>
          )}
        </div>
      }
    >
        {/* Filters */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6" data-testid="match-filters">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="md:col-span-2 lg:col-span-2">
              <Input
                type="text"
                placeholder={t('Search by match ID, teams, or location...')}
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full"
                data-testid="match-search-input"
              />
            </div>
            <Select
              value={statusFilter}
              onChange={(v: string) => { setStatusFilter(v); setCurrentPage(1); }}
              placeholder={t('All Statuses')}
              options={[
                { value: '', label: t('All Statuses') },
                { value: '0', label: t('Scheduled') },
                { value: '1', label: t('In Progress') },
                { value: '2', label: t('Completed') },
                { value: '3', label: t('Closed') },
                { value: '4', label: t('Under Revision') },
                { value: '5', label: t('Cancelled') },
              ]}
              className="w-full"
              data-testid="match-status-filter"
            />
            <Select
              value={locationFilter}
              onChange={(v: string) => { setLocationFilter(v); setCurrentPage(1); }}
              placeholder={t('All Locations')}
              options={[
                { value: '', label: t('All Locations') },
                ...availableLocations.map(location => ({ value: location, label: location })),
              ]}
              className="w-full"
              data-testid="match-location-filter"
            />
            <Select
              value={pitchFilter}
              onChange={(v: string) => { setPitchFilter(v); setCurrentPage(1); }}
              placeholder={t('All Pitches')}
              options={[
                { value: '', label: t('All Pitches') },
                ...availablePitches.map(pitch => ({ value: pitch, label: pitch })),
              ]}
              className="w-full"
              data-testid="match-pitch-filter"
            />
            <Select
              value={ageCategoryFilter}
              onChange={(v: string) => { setAgeCategoryFilter(v); setCurrentPage(1); }}
              placeholder={t('All Age Groups')}
              options={[
                { value: '', label: t('All Age Groups') },
                ...Object.keys(AgeCategory).filter(k => isNaN(Number(k))).map(k => ({
                  value: String(AgeCategory[k as keyof typeof AgeCategory]),
                  label: k,
                })),
              ]}
              className="w-full"
              data-testid="match-age-category-filter"
            />
            <Select
              value={divisionFilter}
              onChange={(v: string) => { setDivisionFilter(v); setCurrentPage(1); }}
              placeholder={t('All Divisions')}
              options={[
                { value: '', label: t('All Divisions') },
                ...availableDivisions.map(division => ({ value: division, label: division })),
              ]}
              className="w-full"
              data-testid="match-division-filter"
            />
            {/* Tournament filter — hidden when page is scoped via URL param */}
            {!tournamentIdParam && (
              <Select
                value={tournamentFilter}
                onChange={(v: string) => { setTournamentFilter(v); setCurrentPage(1); }}
                placeholder={t('All Tournaments')}
                options={tournamentOptions}
                className="w-full"
                data-testid="match-tournament-filter"
              />
            )}
            {/* Referee filter — HR/Admin-only */}
            {isHrOrAdmin && (
              <Select
                value={refereeFilter}
                onChange={(v: string) => { setRefereeFilter(v); setCurrentPage(1); }}
                placeholder={t('All Officials')}
                options={refereeOptions}
                searchable
                className="w-full"
                data-testid="match-referee-filter"
              />
            )}
          </div>
          {(searchTerm || statusFilter || locationFilter || pitchFilter || ageCategoryFilter || divisionFilter || refereeFilter || (!tournamentIdParam && tournamentFilter)) && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {t('Showing {{count}} matches', { count: totalCount })}
              </p>
              <button
                onClick={handleClearFilters}
                className="text-sm text-primary hover:text-primary/80 flex items-center space-x-1 rtl:space-x-reverse focus-visible:ring-2 focus-visible:ring-primary/20 rounded"
                data-testid="match-clear-filters-btn"
              >
                <Icon name="X" size={14} />
                <span>{t('Clear Filters')}</span>
              </button>
            </div>
          )}
        </div>

        {/* Tab Navigation — only show when there are extra tabs */}
        {tournament && (resolveEnum(TournamentFormat, tournament.format) === TournamentFormat.Knockout || resolveEnum(TournamentFormat, tournament.format) === TournamentFormat.League) && (
          <div className="flex gap-1 mb-6 bg-muted/50 p-1 rounded-lg w-fit">
            <button
              onClick={() => setActiveTab('matches')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'matches' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('Matches')}
            </button>
            {resolveEnum(TournamentFormat, tournament.format) === TournamentFormat.Knockout && (
              <button
                onClick={() => setActiveTab('bracket')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'bracket' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('Bracket')}
              </button>
            )}
            {resolveEnum(TournamentFormat, tournament.format) === TournamentFormat.League && (
              <button
                onClick={() => setActiveTab('standings')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'standings' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('Standings')}
              </button>
            )}
          </div>
        )}

        {activeTab === 'matches' && (
          <>
            {/* Loading State */}
            {loading && (
              <div className="py-12">
                <LoadingState title={t('Loading matches...')} size="md" />
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <div className="flex items-start">
                  <Icon name="AlertCircle" size={20} className="text-red-600 me-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-red-900 mb-1">{t('Error loading matches')}</h3>
                    <p className="text-sm text-red-700">{error}</p>
                    <Button variant="outline" size="sm" onClick={refetch} className="mt-3" data-testid="match-retry-btn">
                      {t('Try Again')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Match Table */}
            {!loading && !error && (
              <TournamentMatchTable
                matches={matches}
                sortConfig={sortConfig}
                onSort={handleSort}
                isReferee={isReferee}
                currentUserId={currentUserId}
                isHr={isHr}
                onViewDetails={(match: MatchView) => {
                  setSelectedMatch(match);
                  setShowDetailModal(true);
                }}
                onEditMatch={(match: MatchView) => {
                  setSelectedMatch(match);
                  setShowEditModal(true);
                }}
                onDeleteMatch={(match: MatchView) => {
                  setSelectedMatch(match);
                  setShowDeleteModal(true);
                }}
                onCompleteMatch={(match: MatchView) => { void openCompleteModal(match); }}
                onReschedule={(match: MatchView) => {
                  setSelectedMatch(match);
                  setShowRescheduleModal(true);
                }}
                onChangeStatus={handleChangeStatus}
              />
            )}

            {/* Pagination */}
            {!loading && !error && totalCount > 0 && (
                <div className="mt-6">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(p) => setCurrentPage(p)}
                        variant="full"
                        showEdges={true}
                        pageSize={itemsPerPage}
                        onPageSizeChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }}
                        totalCount={totalCount}
                    />
                </div>
            )}
          </>
        )}

        {activeTab === 'bracket' && tournamentId && (
          <BracketTab tournamentId={tournamentId} tournament={tournament ?? undefined} />
        )}

        {activeTab === 'standings' && tournamentId && (
          <StandingsTab tournamentId={tournamentId} />
        )}

      {/* Modals */}
      <MatchDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        match={selectedMatch}
        isReferee={isReferee}
        currentUserId={currentUserId}
        isHr={isHr}
      />
      <MatchFormModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => refetch()}
        tournamentId={tournamentId}
      />
      <MatchFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={() => refetch()}
        tournamentId={tournamentId}
        match={selectedMatch}
        isHr={isHr}
      />
      <DeleteMatchModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedMatch(null);
        }}
        onDelete={(matchId: number) => handleDeleteMatch(matchId)}
        match={selectedMatch}
      />

      <ChangeStatusModal
        isOpen={pendingStatus != null}
        match={selectedMatch}
        newStatus={pendingStatus}
        onClose={() => {
          setPendingStatus(null);
          setSelectedMatch(null);
        }}
        onConfirm={submitStatusChange}
      />

      <CompleteMatchModal
        isOpen={showCompleteModal}
        onClose={() => {
          setShowCompleteModal(false);
          setSelectedMatch(null);
        }}
        onComplete={handleCompleteMatch}
        match={selectedMatch}
        isReferee={isReferee}
        currentUserId={currentUserId}
        canRateReferees={canRateReferees}
      />

      <RescheduleMatchModal
        isOpen={showRescheduleModal}
        onClose={() => {
          setShowRescheduleModal(false);
          setSelectedMatch(null);
        }}
        match={selectedMatch}
        onSuccess={() => refetch()}
        showToast={showToast}
      />

      {tournamentId && (
        <BulkImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          tournamentId={tournamentId}
          onSuccess={() => { refetch(); setShowImportModal(false); }}
        />
      )}

      <ClawbackConsentDialog {...clawback.dialogProps} />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
};

export default TournamentMatches;
